/**
 * Moverse por la grilla del POS con las flechas.
 *
 * Está acá afuera y no adentro del componente por lo mismo que
 * `reglaDeAgregar` y `pagoImplicito`: decide QUÉ producto entra al ticket
 * cuando el cajero aprieta Enter, y una decisión así no se verifica leyendo un
 * render. Acá se puede escribir el caso de la última fila incompleta, que es
 * donde todo esto se rompe.
 */
export type Direccion = 'arriba' | 'abajo' | 'izquierda' | 'derecha'

/**
 * Dónde queda la marca después de una flecha.
 *
 * `null` es «no hay nada marcado», que es el estado normal: mientras el cajero
 * tipea no hay ningún producto elegido y el Enter sigue haciendo lo de
 * siempre (la etiqueta de la balanza, el código, el único resultado). La marca
 * aparece recién cuando alguien aprieta una flecha, y eso es deliberado:
 * marcar solo el primero de la lista haría que un Enter apurado agregue un
 * producto que nadie eligió.
 *
 * **No hay vuelta al principio.** Llegar al final y seguir apretando ↓ deja la
 * marca en el último, no la manda arriba de todo. En una lista que se recorre
 * a ciegas mientras se atiende, envolver es exactamente cómo se agrega el
 * producto equivocado.
 */
export function moverMarca(
  marcado: number | null,
  direccion: Direccion,
  cuantos: number,
  columnas: number,
): number | null {
  if (cuantos <= 0) return null

  const cols = Math.max(1, columnas)
  const ultimo = cuantos - 1

  // Entrar a la grilla: hacia adelante se empieza por el primero y hacia
  // atrás por el último, que es lo que hace cualquier lista.
  if (marcado === null) {
    return direccion === 'arriba' || direccion === 'izquierda' ? ultimo : 0
  }

  const actual = Math.min(Math.max(marcado, 0), ultimo)

  if (direccion === 'derecha') return Math.min(actual + 1, ultimo)
  if (direccion === 'izquierda') return Math.max(actual - 1, 0)

  if (direccion === 'abajo') {
    // Bajar desde la anteúltima fila puede caer afuera si la última está
    // incompleta —y una verdulería filtra «manzana» y le quedan cinco—, así
    // que ahí se va al último. Quedarse quieto se leería como una tecla que no
    // anda.
    const destino = actual + cols
    return destino > ultimo ? ultimo : destino
  }

  const destino = actual - cols
  return destino < 0 ? 0 : destino
}
